/* Loads exactly the data the current document needs (role-aware, via data/hooks + shared query keys). */
import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useData } from "../../data";
import {
  qk, useAllEmployees, useEmployee, useEosbQuote, useLeaveBalance, useLeavePolicies, useLeaves, useLetters,
  useLoans, useOffboarding, usePayrollHistory, usePayrollRun, useSettings, useSettlement, useToday,
} from "../../data/hooks";
import type { DailyAttendance, Employee, Payslip, Role, Tenant, User } from "../../data/types";
import type { PrintData } from "./components/data";
import type { PrState } from "./state";

export const FALLBACK_TENANT: Tenant = { id: "", name: "Al Waha Trading Est.", nameAr: "مؤسسة الواحة التجارية", cr: "4030512874", vat: "310987654300003", city: "Jeddah", category: "" };

export const prevPeriods = (period: string, n: number): string[] => {
  let y = +period.slice(0, 4), m = +period.slice(5, 7);
  const out: string[] = [];
  for (let i = 0; i < n; i++) { m--; if (!m) { m = 12; y--; } out.push(y + "-" + String(m).padStart(2, "0")); }
  return out;
};

export interface PrintDataResult {
  data: PrintData;
  tenant: Tenant;
  today: string;
  runPeriod: string;
  periods: [string, boolean][];     // [period, isDraft]
  allEmps: Employee[];              // selectable employees
  letterTo: string;
  letterRef: string;
  letterKind: string | null;        // resolved from a letter id
  letterEmp: number | null;
  loading: boolean;
  error: unknown;
  refetch: () => void;
}

export function usePrintData(s: PrState, role: Role | null, user: User | null): PrintDataResult {
  const p = useData();
  const staff = role === "owner" || role === "hr" || role === "accountant";
  const oh = role === "owner" || role === "hr";
  const isEmp = role === "employee";
  const ownId = user?.employeeId ?? 0;
  const doc = s.doc;

  const settings = useSettings({ enabled: staff });
  const all = useAllEmployees({ enabled: staff });
  const me = useEmployee(ownId, { enabled: isEmp && ownId > 0 });
  const run = usePayrollRun({ enabled: staff });
  const history = usePayrollHistory({ enabled: staff });

  const today = useToday();
  const runPeriod = run.data?.run.period ?? today.slice(0, 7);

  const allEmps = useMemo<Employee[]>(() => {
    if (isEmp) return me.data ? [me.data] : [];
    return (all.data ?? []).filter((e) => e.status !== "Terminated").slice().sort((a, b) => a.id - b.id);
  }, [isEmp, me.data, all.data]);

  const empId = isEmp ? ownId : s.emp;
  const emp = allEmps.find((e) => e.id === empId) ?? (isEmp ? null : allEmps[0] ?? null);
  const effEmp = emp?.id ?? 0;

  const letters = useLetters({ enabled: (oh || isEmp) && doc === "letter" });
  const letterById = /^\d+$/.test(s.letter) ? letters.data?.find((l) => l.id === Number(s.letter)) : undefined;
  const letterForEmp = letterById ?? letters.data?.find((l) => l.employeeId === effEmp && l.kind === s.letter);

  const loans = useLoans({ enabled: staff && doc === "voucher" });
  const leaves = useLeaves({ enabled: (oh || isEmp) && (doc === "leaveform" || doc === "letter") });
  const policies = useLeavePolicies({ enabled: doc === "leaveform" && !!role });
  const balance = useLeaveBalance(effEmp, { enabled: doc === "leaveform" && effEmp > 0 && !!role && role !== "admin" });

  const offb = useOffboarding({ enabled: oh && doc === "settlement" });
  const offRec = offb.data?.find((o) => o.employeeId === effEmp) ?? null;
  const settlement = useSettlement(offRec?.id ?? 0, { enabled: !!offRec });
  const end = s.date && s.date.length === 10 ? s.date : today;
  const eosb = useEosbQuote(effEmp, end, "termination", { enabled: oh && doc === "settlement" && !!offb.data && !offRec && effEmp > 0 });

  /* payslips for the chosen period (run period → from the run view; others → per employee) */
  const period = doc === "gosi" || doc === "wps" ? runPeriod : (s.period || runPeriod);
  const needSlips = doc === "payslip" || doc === "register" || doc === "cheque" || doc === "gosi" || doc === "wps";
  const fromRun = staff && period === runPeriod && !!run.data;
  const multi = doc === "register" || ((doc === "payslip" || doc === "cheque") && s.scope === "all");
  const slipIds = !needSlips || fromRun ? [] : multi && !isEmp ? allEmps.map((e) => e.id) : effEmp ? [effEmp] : [];
  const slipQs = useQueries({
    queries: slipIds.map((id) => ({ queryKey: qk.payslip(id, period), queryFn: () => p.getPayslip(id, period), enabled: !!role && (staff ? !!run.data || run.isError : true) })),
  });
  const slips = useMemo(() => {
    const m = new Map<number, Payslip>();
    if (fromRun) for (const x of run.data!.slips) m.set(x.employeeId, x);
    else for (const q of slipQs) if (q.data) m.set(q.data.employeeId, q.data);
    return m;
  }, [fromRun, run.data, slipQs]);

  /* attendance month (auto-populated from devices / ESS punches) */
  const attPeriod = (s.date ? s.date.slice(0, 7) : "") || today.slice(0, 7);
  const attIds = oh && doc === "attendance" ? allEmps.filter((e) => !s.dept || e.dept === s.dept).map((e) => e.id) : [];
  const attQs = useQueries({ queries: attIds.map((id) => ({ queryKey: qk.attendanceMonth(id, attPeriod), queryFn: () => p.attendanceMonth(id, attPeriod) })) });
  const attendance = useMemo(() => {
    const m = new Map<number, DailyAttendance[]>();
    attQs.forEach((q, i) => { if (q.data) m.set(attIds[i], q.data); });
    return m;
  }, [attQs]); // eslint-disable-line react-hooks/exhaustive-deps

  const periods = useMemo<[string, boolean][]>(() => {
    if (isEmp) return [[today.slice(0, 7), false] as [string, boolean], ...prevPeriods(today.slice(0, 7), 6).map((x) => [x, false] as [string, boolean])];
    const hist = (history.data ?? []).slice(-6).reverse().map((h) => h.id).filter((x) => x !== runPeriod);
    return [...hist.map((x) => [x, false] as [string, boolean]), [runPeriod, !run.data?.run.paid]];
  }, [isEmp, today, history.data, runPeriod, run.data]);

  const loadingList = [
    staff && (settings.isLoading || all.isLoading || run.isLoading),
    isEmp && me.isLoading,
    slipQs.some((q) => q.isLoading),
    attQs.some((q) => q.isLoading),
    doc === "letter" && letters.isLoading,
    doc === "voucher" && loans.isLoading,
    doc === "leaveform" && (leaves.isLoading || balance.isLoading),
    doc === "settlement" && (offb.isLoading || settlement.isLoading || eosb.isLoading),
  ];
  const error = (staff ? settings.error || all.error || run.error : null) || (isEmp ? me.error : null) || slipQs.find((q) => q.error)?.error || null;

  const data: PrintData = {
    emps: allEmps, emp, slips, run: run.data ?? null, gosi: settings.data?.gosi ?? null, payDay: settings.data?.payDay ?? 27,
    loans: loans.data ?? [], leaves: leaves.data ?? [], balance: balance.data ?? null, policies: policies.data ?? [],
    attendance, attPeriod, weekendDays: settings.data?.attendance.weekendDays ?? [5],
    offboarding: offRec, settlement: settlement.data ?? null, eosb: eosb.data ?? null,
  };

  return {
    data, tenant: settings.data?.tenant ?? FALLBACK_TENANT, today, runPeriod, periods, allEmps,
    letterTo: letterForEmp?.to ?? "", letterRef: letterForEmp ? "HR/" + letterForEmp.date.slice(0, 4) + "/" + letterForEmp.id : "",
    letterKind: letterById?.kind ?? null, letterEmp: letterById?.employeeId ?? null,
    loading: loadingList.some(Boolean), error,
    refetch: () => { void settings.refetch(); void all.refetch(); void run.refetch(); },
  };
}
